import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { AuthError, AuthScreen } from '../components/AuthScreen';
import { useErrorMessage } from '../lib/useErrorMessage';

export function LoginPage(): ReactNode {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const location = useLocation();
  const describeError = useErrorMessage();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  const message = describeError(error);

  return (
    <AuthScreen subtitle={t('auth.platform')}>
      <form onSubmit={(e) => void onSubmit(e)}>
        <input
          type="email"
          className="login-field"
          placeholder={t('login.email')}
          aria-label={t('login.email')}
          autoComplete="username"
          autoFocus
          required
          disabled={submitting}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          type="password"
          className="login-field"
          placeholder={t('login.password')}
          aria-label={t('login.password')}
          autoComplete="current-password"
          required
          disabled={submitting}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {message ? <AuthError message={message} /> : null}

        <button type="submit" className="login-btn" disabled={submitting}>
          {submitting ? t('login.submitting') : t('login.submit')}
        </button>
      </form>
    </AuthScreen>
  );
}
